/**
 * DraftFirstControls — the [Approve & send] [Edit] [Skip] slice of a
 * suggests/do-now card that carries a FINISHED drafted email.
 *
 * The inversion mandate: instead of nagging the operator to go do machine
 * work ("Queue reminder task"), the card hands over the actual artifact —
 * an expandable subject + body preview composed strictly from real record
 * data (src/lib/agent/draftFirst.ts). Every send stays approval-gated:
 * "Approve & send" NEVER writes the outbox directly; it queues the
 * `send_drafted_email` proposal into the EXISTING approvals inbox
 * (`queueApproval` → Dashboard → `resolveApproval`), where the executor
 * owns the outbox write and its undo-cancel. "Edit" lets the operator
 * rewrite the subject/body inline BEFORE queueing (the HTML re-renders
 * from the edited text through the same escaping path). "Skip" dismisses
 * the suggestion via the parent.
 */

import { useState } from 'react';
import { Check, ChevronDown, ChevronUp, Loader2, Mail, Pencil, X } from 'lucide-react';
import { C } from '../lib/tokens';
import { queueApproval } from '../lib/agent/approvals';
import {
  DRAFT_INTENT_LABEL,
  draftTextToHtml,
  type NudgeDraftState,
} from '../lib/agent/draftFirst';

/** The composed-draft variant of {@link NudgeDraftState}. */
export type ReadyDraft = Extract<NudgeDraftState, { state: 'draft' }>;

export interface DraftFirstControlsProps {
  draft: ReadyDraft;
  /** Provenance note stored on the proposal ("why this draft exists"). */
  note?: string;
  /** Parent-owned dismissal — hides the suggestion without queueing. */
  onSkip: () => void;
}

type QueueState = 'idle' | 'queuing' | 'queued' | 'failed';

export function DraftFirstControls({ draft, note, onSkip }: DraftFirstControlsProps) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(draft.action.subject);
  const [body, setBody] = useState(draft.action.text);
  const [queueState, setQueueState] = useState<QueueState>('idle');

  const intentLabel = DRAFT_INTENT_LABEL[draft.action.intent];

  const approve = async () => {
    if (queueState === 'queuing' || queueState === 'queued') return;
    setQueueState('queuing');
    try {
      // Approval-gated by construction: this queues a proposal into the
      // EXISTING inbox — the outbox write happens only on inbox approval.
      await queueApproval(
        {
          ...draft.action,
          subject,
          text: body,
          // Re-render the HTML from the (possibly edited) text so what the
          // operator read is exactly what goes out.
          html: draftTextToHtml(body),
        },
        'autopilot',
        note,
      );
      setQueueState('queued');
    } catch {
      setQueueState('failed');
    }
  };

  if (queueState === 'queued') {
    return (
      <span
        className="glass-pill glass-pill--green"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Check size={11} />
        Draft queued — approve in the Dashboard inbox
      </span>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          className="glass-pill glass-pill--blue"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          title={`Composed from this record's real data — nothing invented. Goes to ${draft.recipient.email}.`}
        >
          <Mail size={11} />
          Draft ready: {intentLabel} → {draft.recipient.email}
        </span>
        <button
          type="button"
          className="glass-btn"
          onClick={() => setExpanded((e) => !e)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px' }}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? 'Hide draft' : 'Preview draft'}
        </button>
      </div>

      {expanded && (
        <div
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            background: 'rgba(0,0,0,0.25)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gap: 6,
          }}
        >
          {editing ? (
            <>
              <input
                aria-label="Draft subject"
                className="glass-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                style={{ width: '100%', fontSize: 12 }}
              />
              <textarea
                aria-label="Draft body"
                className="glass-input"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                style={{ width: '100%', fontSize: 12, resize: 'vertical' }}
              />
            </>
          ) : (
            <>
              <div className="t-card-title" style={{ fontSize: 12 }}>
                {subject}
              </div>
              <pre
                className="t-meta"
                style={{
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  color: C.textMuted,
                }}
              >
                {body}
              </pre>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="glass-btn glass-btn--primary"
          disabled={queueState === 'queuing'}
          onClick={() => void approve()}
          title="Queues this exact email as a governed proposal — nothing sends until you approve it in the Dashboard inbox"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          {queueState === 'queuing' ? (
            <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
          ) : (
            <Check size={12} />
          )}
          {queueState === 'failed' ? 'Retry — Approve & send' : 'Approve & send'}
        </button>
        <button
          type="button"
          className="glass-btn"
          onClick={() => {
            setEditing((e) => !e);
            setExpanded(true);
          }}
          title="Edit the subject and body inline before queueing"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Pencil size={12} />
          {editing ? 'Done editing' : 'Edit'}
        </button>
        <button
          type="button"
          className="glass-btn"
          onClick={onSkip}
          title="Dismiss this suggestion — queues nothing"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <X size={12} />
          Skip
        </button>
      </div>
    </div>
  );
}

/** The honest no-address state: a draft would exist, but there is no email
 *  on file — shown instead of a draft, never a guessed recipient. */
export function NeedsEmailPill({ personName }: { personName: string }) {
  return (
    <span
      className="glass-pill glass-pill--orange"
      title="A draft is ready to compose, but this record has no contact email. Add a POC email to get the finished artifact here."
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <Mail size={11} />
      Needs email — no address on file for {personName}
    </span>
  );
}
